import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Form } from 'antd'
import { Message } from '@/components/message'
import moment from 'moment'
import type { GetTemplateList, GetCaseList } from '@/api/waf-create-task'
import { TemplateItem, CaseItem } from './interface'
import { defaultFormState } from './consts'
import {
  useTaskGroup,
  useGetEmailList,
  useGetAgentList,
  useCreateTask,
  useAttackTypeList,
  useSaveCaseToTemplate,
  useGetTemplateList,
  useGetCaseList,
  useResourceTagList,
  useGetAllTemplateId,
  useGetAllCaseId,
  useGetResourceIdsByTemplateIds,
  useGetSecurityDeviceList,
  useGetTaskDetail,
} from './utils'
import {
  handleExecuteTypeChange,
  handleExecuteTemplateLatestCaseChange,
  handleTemplateSwitchChange,
  handleFormValueChange as formValueChangeHandler,
  buildCreateTaskParams,
  handleSelectAll,
  handleCaseSelectAll,
  saveTemplateSubmit,
} from './utils/formHandlers'
import { createTemplateColumns, createSelectedCaseColumns } from './utils/tableColumns'
import { createTemplateRowSelection, createCaseRowSelection } from './utils/rowSelection'
import { handleTemplateTableChange, handleCaseTableChange } from './utils/tableHandlers'
import { createSearchOptions, createTemplateSearchOptions } from './utils/searchOptions'
import { createCascaderDeviceListData } from './utils/deviceUtils'
import { t } from '@/i18n'

const { useForm } = Form

// 表单状态管理hook
export const useFormState = () => {
  const [formState, setFormState] = useState(defaultFormState)
  const [createForm] = useForm()
  const [form] = useForm()

  const handleFormValueChangeCallback = useCallback(
    (changedValues: any, allValues: any) => {
      formValueChangeHandler(changedValues, allValues, setFormState)
    },
    [setFormState],
  )

  return {
    formState,
    setFormState,
    createForm,
    form,
    handleFormValueChange: handleFormValueChangeCallback,
  }
}

// 步骤管理hook
export const useStepManagement = (
  setSelectedTemplateIds?: (ids: string[]) => void,
  setSelectedTemplateData?: (data: any[]) => void,
  setSelectedCaseIds?: (ids: string[]) => void,
) => {
  const [step, setStep] = useState(1)

  const handleBack = useCallback(() => {
    setStep(step - 1)
    // 清空所有选中的模板和用例数据
    if (setSelectedTemplateIds) setSelectedTemplateIds([])
    if (setSelectedTemplateData) setSelectedTemplateData([])
    if (setSelectedCaseIds) setSelectedCaseIds([])
    console.log(t('清空模板和用例选择，保留第一步表单信息'))
  }, [step, setSelectedTemplateIds, setSelectedTemplateData, setSelectedCaseIds])

  const submitForm = useCallback(() => {
    setStep(2)
  }, [])

  return {
    step,
    setStep,
    handleBack,
    submitForm,
  }
}

// 设备数据管理hook
export const useDeviceData = (getSecurityDeviceList?: any) => {
  const [, setTargetList] = useState<any[]>([])
  const [securityDeviceData, setSecurityDeviceData] = useState<any[]>([])

  const cascaderDeviceListData = useMemo(
    () => createCascaderDeviceListData(securityDeviceData),
    [securityDeviceData],
  )

  // 获取安全设备列表的函数
  const fetchSecurityDeviceList = useCallback(
    async (agentId?: string) => {
      if (getSecurityDeviceList && agentId) {
        try {
          const result = await getSecurityDeviceList({ agentId, scene: 'SCENEID_WEBGATEWAY' })
          if (result?.data?.deviceList) {
            setSecurityDeviceData(result.data.deviceList)
          }
        } catch (error) {
          console.error(t('获取安全设备列表失败:'), error)
          setSecurityDeviceData([])
        }
      }
    },
    [getSecurityDeviceList],
  )

  return {
    setTargetList,
    securityDeviceData,
    setSecurityDeviceData,
    cascaderDeviceListData,
    fetchSecurityDeviceList,
  }
}

// 模板数据管理hook
export const useTemplateData = () => {
  const [, setSelectedTemplate] = useState<any[]>([])
  const [executeTemplateLatestCase, setExecuteTemplateLatestCase] = useState(false)
  const [templateValue, setTemplateValue] = useState<{
    field: string | number
    value: string | number | string[] | number[] | undefined
  }>({
    field: '',
    value: '',
  })

  const [templateData, setTemplateData] = useState<{
    list: TemplateItem[]
    total: number
  }>({
    list: [],
    total: 0,
  })

  const [searchTemplateParams, setSearchTemplateParams] = useState<GetTemplateList>({
    page: 1,
    pageSize: 10,
  })

  const [selectedTemplateIds, setSelectedTemplateIds] = useState<string[]>([])
  const [selectedTemplateData, setSelectedTemplateData] = useState<TemplateItem[]>([])
  const [templateIds, setTemplateIds] = useState<string[]>([])

  return {
    setSelectedTemplate,
    executeTemplateLatestCase,
    setExecuteTemplateLatestCase,
    templateValue,
    setTemplateValue,
    templateData,
    setTemplateData,
    searchTemplateParams,
    setSearchTemplateParams,
    selectedTemplateIds,
    setSelectedTemplateIds,
    selectedTemplateData,
    setSelectedTemplateData,
    templateIds,
    setTemplateIds,
  }
}

// 用例数据管理hook
export const useCaseData = () => {
  const [caseValue, setCaseValue] = useState<{
    field: string | number
    value: string | number | string[] | number[] | undefined
  }>({
    field: '',
    value: '',
  })

  const [caseData, setCaseData] = useState<{
    list: CaseItem[]
    total: number
  }>({
    list: [],
    total: 0,
  })

  const [searchCaseParams, setSearchCaseParams] = useState<GetCaseList>({
    page: 1,
    pageSize: 10,
  })

  const [selectedCaseIds, setSelectedCaseIds] = useState<string[]>([])
  const [caseKey, setCaseKey] = useState(0)
  const [caseIds, setCaseIds] = useState<string[]>([])
  // 维护每个模板的已选用例数映射
  const [templateSelectedCountMap, setTemplateSelectedCountMap] = useState<Record<string, number>>(
    {},
  )

  // 更新模板已选用例数映射
  const updateTemplateSelectedCountMap = useCallback(
    (newSelectedCaseIds: string[]) => {
      // 基于caseData.list计算每个模板的已选用例数
      const updatedMap: Record<string, number> = {}
      const selectedIdSet = new Set(newSelectedCaseIds)

      caseData.list.forEach(item => {
        if (!selectedIdSet.has(item.resourceId.toString())) return
        item.templateIds?.forEach(templateId => {
          if (!templateId) return
          updatedMap[templateId] = (updatedMap[templateId] || 0) + 1
        })
      })

      setTemplateSelectedCountMap(updatedMap)
    },
    [caseData.list],
  )

  return {
    caseValue,
    setCaseValue,
    caseData,
    setCaseData,
    searchCaseParams,
    setSearchCaseParams,
    selectedCaseIds,
    setSelectedCaseIds,
    caseKey,
    setCaseKey,
    caseIds,
    setCaseIds,
    templateSelectedCountMap,
    setTemplateSelectedCountMap,
    updateTemplateSelectedCountMap,
  }
}

// 模态框状态管理hook
export const useModalState = () => {
  const [saveTemplateModal, setSaveTemplateModal] = useState(false)
  const [detailVisible, setDetailVisible] = useState<{
    visible: boolean
    resourceId: string
  }>({
    visible: false,
    resourceId: '',
  })

  return {
    saveTemplateModal,
    setSaveTemplateModal,
    detailVisible,
    setDetailVisible,
  }
}

// 选择器状态管理hook
export const useSelectState = () => {
  const [templateTypeSelectOpen, setTemplateTypeSelectOpen] = useState(false)
  const [riskLevelSelectOpen, setRiskLevelSelectOpen] = useState(false)
  const [resourceLevelSelectOpen, setResourceLevelSelectOpen] = useState(false)
  const [attackTypeSelectOpen, setAttackTypeSelectOpen] = useState(false)

  return {
    templateTypeSelectOpen,
    setTemplateTypeSelectOpen,
    riskLevelSelectOpen,
    setRiskLevelSelectOpen,
    resourceLevelSelectOpen,
    setResourceLevelSelectOpen,
    attackTypeSelectOpen,
    setAttackTypeSelectOpen,
  }
}

// API数据获取hook
export const useApiData = () => {
  const [taskGroup, getTaskGroup] = useTaskGroup()
  const [emailList] = useGetEmailList()
  const [agentList, getAgentList] = useGetAgentList()
  const [attackTypeList, getAttackTypeList] = useAttackTypeList()
  const [createTask, createTaskLoading] = useCreateTask()
  const [saveCaseToTemplate, saveCaseToTemplateLoading] = useSaveCaseToTemplate()
  const [getTemplateList, templateLoading] = useGetTemplateList()
  const [getCaseList, caseLoading] = useGetCaseList()
  const [resourceTagList, getResourceTagList] = useResourceTagList()
  const [getAllTemplateId] = useGetAllTemplateId()
  const [getAllCaseId] = useGetAllCaseId()
  const [, getResourceIdsByTemplateIds] = useGetResourceIdsByTemplateIds()
  const [securityDeviceList, getSecurityDeviceList] = useGetSecurityDeviceList()
  const [getTaskDetail, taskDetailData] = useGetTaskDetail()

  return {
    taskGroup,
    getTaskGroup,
    emailList,
    getAgentList,
    agentList,
    attackTypeList,
    getAttackTypeList,
    createTask,
    createTaskLoading,
    saveCaseToTemplate,
    saveCaseToTemplateLoading,
    getTemplateList,
    templateLoading,
    getCaseList,
    caseLoading,
    resourceTagList,
    getResourceTagList,
    getAllTemplateId,
    getAllCaseId,
    getResourceIdsByTemplateIds,
    securityDeviceList,
    getSecurityDeviceList,
    getTaskDetail,
    taskDetailData,
  }
}

// 根据agentId找到对应的层级路径
const findAgentPath = (agentId: string, agentList: any[]) => {
  console.log(t('查找agent路径，agentId:'), agentId, 'agentList:', agentList)

  for (const domain of agentList) {
    for (const os of domain.os) {
      for (const agent of os.agents) {
        if (agent.agentId === agentId) {
          console.log(t('找到匹配的agent:'), agent)
          // 只返回二级路径：domainId -> agentId
          const path = [domain.id, agent.agentId]
          console.log(t('Agent二级路径:'), path)
          return path
        }
      }
    }
  }
  console.log(t('未找到匹配的agent路径'))
  return []
}

// 根据deviceId找到对应的层级路径
const findDevicePath = (deviceId: string, securityDeviceData: any[]) => {
  console.log(t('查找设备路径，deviceId:'), deviceId, 'securityDeviceData:', securityDeviceData)

  for (const deviceType of securityDeviceData) {
    for (const device of deviceType.devices) {
      if (device.deviceId === deviceId) {
        const path = [deviceType.deviceType, device.deviceId] // 二级路径：设备类型 -> 设备ID
        console.log(t('找到匹配的设备路径:'), path)
        return path
      }
    }
  }
  console.log(t('未找到匹配的设备路径'))
  return []
}

// 处理周期任务的时间配置
const processCronSchedule = (schedulerType: string, cron: any) => {
  let dayOfWeek: number[] = []
  let dayOfMonth: number[] = []
  let timeOfDay: any = null

  if (schedulerType === 'scheduler_type_daily') {
    // 每日任务
    if (cron.timeOfDay) {
      console.log(t('每日任务原始时间:'), cron.timeOfDay)
      // 使用moment创建时间对象
      timeOfDay = moment(cron.timeOfDay, 'HH:mm:ss')
      console.log(t('每日任务转换后时间:'), timeOfDay)
    }
  } else if (schedulerType === 'scheduler_type_weekly') {
    // 周任务
    if (cron.dayOfWeek && cron.dayOfWeek.length > 0) {
      dayOfWeek = cron.dayOfWeek
    }
    if (cron.timeOfDay) {
      timeOfDay = moment(cron.timeOfDay, 'HH:mm:ss')
    }
  } else if (schedulerType === 'scheduler_type_monthly') {
    // 月任务
    if (cron.dayOfMonth && cron.dayOfMonth.length > 0) {
      dayOfMonth = cron.dayOfMonth
    }
    if (cron.timeOfDay) {
      timeOfDay = moment(cron.timeOfDay, 'HH:mm:ss')
    }
  }

  return { dayOfWeek, dayOfMonth, timeOfDay }
}

// 处理调度类型和时间
const processSchedulerInfo = (taskData: any) => {
  let executeType = 1
  let schedulerType = ''
  let taskTime: any = null
  let dayOfWeek: number[] = []
  let dayOfMonth: number[] = []
  let timeOfDay: any = null

  if (taskData.schedulerType === 'scheduler_type_immediate') {
    executeType = 1
  } else if (taskData.schedulerType === 'scheduler_type_reserve') {
    executeType = 2
    if (taskData.taskTime) {
      taskTime = moment(taskData.taskTime)
    }
  } else {
    executeType = 3
    schedulerType = taskData.schedulerType

    if (taskData.cronSchedule) {
      const cronResult = processCronSchedule(schedulerType, taskData.cronSchedule)
      dayOfWeek = cronResult.dayOfWeek
      dayOfMonth = cronResult.dayOfMonth
      timeOfDay = cronResult.timeOfDay

      // 对于周期任务，需要将timeOfDay映射到taskTime字段
      if (timeOfDay) {
        console.log(`${schedulerType}${t('任务时间映射:')}`, timeOfDay)
        taskTime = timeOfDay
      }
    }
  }

  return { executeType, schedulerType, taskTime, dayOfWeek, dayOfMonth, timeOfDay }
}

// 处理安全设备数据
const handleSecurityDeviceData = async (
  taskData: any,
  agentIdValue: any[],
  fetchSecurityDeviceList: any,
  getSecurityDeviceList: any,
  createForm: any,
  setFormState: any,
) => {
  if (agentIdValue.length > 0 && fetchSecurityDeviceList) {
    const selectedAgentId = agentIdValue.length >= 2 ? agentIdValue[1] : agentIdValue[0]
    if (selectedAgentId) {
      console.log(t('获取安全设备列表，agentId:'), selectedAgentId)
      await fetchSecurityDeviceList(String(selectedAgentId))
      await new Promise(resolve => setTimeout(resolve, 500))

      const latestSecurityDeviceData = await getSecurityDeviceList({
        agentId: selectedAgentId,
        scene: 'SCENEID_WEBGATEWAY',
      })

      console.log(t('获取到的安全设备数据:'), latestSecurityDeviceData)

      if (latestSecurityDeviceData?.data?.deviceList && taskData.deviceId) {
        const deviceIdsValue = findDevicePath(
          taskData.deviceId,
          latestSecurityDeviceData.data.deviceList,
        )
        console.log(t('安全设备层级路径:'), deviceIdsValue)

        setTimeout(() => {
          console.log(t('设置安全设备表单值:'), deviceIdsValue)
          createForm.setFieldsValue({ deviceIds: deviceIdsValue })
          setFormState(prev => ({ ...prev, deviceIds: deviceIdsValue }))
        }, 300)
      } else {
        console.log(t('安全设备数据获取失败或deviceId为空:'), {
          hasData: !!latestSecurityDeviceData?.data?.deviceList,
          deviceListLength: latestSecurityDeviceData?.data?.deviceList?.length || 0,
          deviceId: taskData.deviceId,
        })
      }
    }
  }
}

// 获取任务基础信息并填充表单
const getTaskBaseInfo = async (
  taskId: string,
  getTaskDetail: any,
  setFormState: any,
  createForm: any,
  agentList: any[] = [],
  securityDeviceData: any[] = [],
  fetchSecurityDeviceList: any,
  getSecurityDeviceList: any,
) => {
  try {
    console.log(t('开始获取任务详情，任务ID:'), taskId)
    const result = await getTaskDetail({ id: taskId })
    if (result?.data) {
      const taskData = result.data
      console.log(t('获取到任务详情:'), taskData)

      // 处理调度类型和时间
      const schedulerInfo = processSchedulerInfo(taskData)

      // 处理agent的层级值
      let agentIdValue: any[] = []
      if (taskData.agentToTi?.agentId && agentList.length > 0) {
        agentIdValue = findAgentPath(taskData.agentToTi.agentId, agentList)
        console.log(t('Agent层级路径:'), agentIdValue)

        // 处理安全设备数据
        await handleSecurityDeviceData(
          taskData,
          agentIdValue,
          fetchSecurityDeviceList,
          getSecurityDeviceList,
          createForm,
          setFormState,
        )
      }

      // 处理安全设备的层级值（如果没有通过agent获取到）
      let deviceIdsValue: any[] = []
      if (taskData.deviceId && securityDeviceData.length > 0) {
        deviceIdsValue = findDevicePath(taskData.deviceId, securityDeviceData)
        console.log(t('安全设备层级路径:'), deviceIdsValue)
      } else {
        console.log(t('安全设备数据未加载或deviceId为空:'), {
          deviceId: taskData.deviceId,
          securityDeviceDataLength: securityDeviceData.length,
          securityDeviceData: securityDeviceData,
        })
      }

      // 填充基本表单信息
      const formValues = {
        name: taskData.name ? `${taskData.name}` : '',
        groupId: taskData.groupId,
        ...schedulerInfo,
        priority: taskData.priority,
        speed: taskData.speed,
        timeout: taskData.timeout,
        executionTimes: taskData.executeTimesLimit === 0 ? undefined : taskData.executeTimesLimit,
        agentId: agentIdValue,
        hostId: taskData.agentToTi?.hostId || '1',
        dnsSinkhole: taskData.agentToTi?.dnsSinkhole,
        deviceIds: deviceIdsValue,
        protocol: taskData.proxy?.address?.split('://')[0] || 'http',
        proxy: taskData.proxy?.address?.split('://')[1] || '',
        account: taskData?.proxy?.account,
        password: taskData?.proxy?.password,
        enableAuth: taskData?.proxy?.enableAuth || false,
        send: taskData.reportConfig?.send || false,
        filter: taskData.reportConfig?.filter || false,
        condition: taskData.reportConfig?.condition,
        emailList: taskData.reportConfig?.emailList?.map(email => email.id) || [],
        executeTemplateLatestCase:
          taskData.templateList?.some(template => template.useLatestResource) || false,
      }

      // 设置表单值
      createForm.setFieldsValue(formValues)

      // 更新表单状态
      setFormState(prev => ({
        ...prev,
        ...formValues,
        verifyMode: [2], // 默认验证模式
        enableAuth: taskData?.proxy?.enableAuth || false,
      }))
    }
  } catch (error) {
    console.error(t('获取任务详情失败:'), error)
  }
}

// 初始化数据hook
export const useInitialData = (
  getAgentList: any,
  getTaskGroup: any,
  getAttackTypeList: any,
  setFormState: any,
  createForm: any,
  getTaskDetail: any,
  agentList: any[] = [],
  securityDeviceData: any[] = [],
  fetchSecurityDeviceList: any,
  getSecurityDeviceList: any,
) => {
  const { state: stateParams } = useLocation()
  const hasCopiedRef = useRef(false) // 用于防止重复复制

  useEffect(() => {
    getAgentList().catch(_ => {})
    getTaskGroup({ page: 1, limit: 100 })
      .then(res => {
        const groupId = res.data.groups.find(item => item.groupName === '常规任务')?.groupId
        console.log('groupId', groupId)
        setFormState(prev => ({
          ...prev,
          groupId: groupId,
        }))
        createForm.setFieldsValue({
          groupId: groupId,
        })
      })
      .catch(_ => {})
  }, [getAgentList, getTaskGroup, setFormState, createForm])

  useEffect(() => {
    if (
      stateParams &&
      typeof stateParams === 'object' &&
      'id' in stateParams &&
      stateParams.id &&
      !hasCopiedRef.current && // 防止重复执行
      agentList.length > 0 // 确保agentList已经加载完成
    ) {
      console.log(t('检测到复制任务，任务ID:'), stateParams.id)
      console.log(t('AgentList已加载，长度:'), agentList.length)
      hasCopiedRef.current = true // 标记已执行

      // 延迟执行，确保数据已经加载完成
      const timer = setTimeout(() => {
        getTaskBaseInfo(
          stateParams.id as string,
          getTaskDetail,
          setFormState,
          createForm,
          agentList,
          securityDeviceData,
          fetchSecurityDeviceList,
          getSecurityDeviceList,
        ).catch(_ => {})
      }, 100)

      return () => clearTimeout(timer)
    }
    return undefined // 明确返回undefined
  }, [(stateParams as any)?.id, agentList.length]) // 依赖任务ID和agentList长度
}

// 搜索选项hook
export const useSearchOptions = (resourceTagList: any) => {
  const options = useMemo(() => createSearchOptions(resourceTagList), [resourceTagList])
  const templateSearchOptions = useMemo(() => createTemplateSearchOptions(), [])

  return {
    options,
    templateSearchOptions,
  }
}

// 表单处理hook
export const useFormHandlers = (
  formState: any,
  setFormState: any,
  createForm: any,
  setTemplateData: any,
  setSelectedTemplateData: any,
  selectedCaseIds: any,
  templateSelectedCountMap: any,
  templateData: any,
) => {
  const onExecuteTypeChange = useCallback(
    (value: number) => {
      handleExecuteTypeChange(value, setFormState, createForm)
    },
    [setFormState, createForm],
  )

  const onExecuteTemplateLatestCaseChange = useCallback(
    (checked: boolean) => {
      handleExecuteTemplateLatestCaseChange(
        checked,
        setFormState,
        createForm,
        setTemplateData,
        setSelectedTemplateData,
      )
    },
    [setFormState, createForm, setTemplateData, setSelectedTemplateData],
  )

  const onTemplateSwitchChange = useCallback(
    (templateId: string, checked: boolean) => {
      handleTemplateSwitchChange(
        templateId,
        checked,
        formState,
        selectedCaseIds,
        setTemplateData,
        setSelectedTemplateData,
        templateSelectedCountMap,
        templateData,
      )
    },
    [
      formState,
      selectedCaseIds,
      setTemplateData,
      setSelectedTemplateData,
      templateSelectedCountMap,
      templateData,
    ],
  )

  return {
    onExecuteTypeChange,
    onExecuteTemplateLatestCaseChange,
    onTemplateSwitchChange,
  }
}

// 表格处理hook
export const useTableHandlers = (
  caseValue: any,
  searchCaseParams: any,
  setSearchCaseParams: any,
  setSelectedCaseIds: any,
  searchTemplateParams: any,
  setSearchTemplateParams: any,
  setSelectedTemplateIds: any,
  selectedCaseIds: any,
  caseIds: any,
  getCaseList: any,
  selectedTemplateIds: any,
  caseData: any,
  setTemplateSelectedCountMap: any,
  formState: any,
) => {
  const onCaseTableChange = useCallback(
    (pagination: any, filters: any, sorter: any) => {
      handleCaseTableChange(
        pagination,
        filters,
        sorter,
        caseValue,
        searchCaseParams,
        setSearchCaseParams,
        setSelectedCaseIds,
      )
    },
    [caseValue, searchCaseParams, setSearchCaseParams, setSelectedCaseIds],
  )

  const onTemplateTableChange = useCallback(
    (pagination: any, filters: any, sorter: any) => {
      handleTemplateTableChange(
        pagination,
        filters,
        sorter,
        searchTemplateParams,
        setSearchTemplateParams,
        setSelectedTemplateIds,
      )
    },
    [searchTemplateParams, setSearchTemplateParams, setSelectedTemplateIds],
  )

  const onCaseSelectAll = useCallback(
    async (checked: boolean) => {
      await handleCaseSelectAll(
        checked,
        selectedCaseIds,
        caseIds,
        setSelectedCaseIds,
        getCaseList,
        searchCaseParams,
        selectedTemplateIds,
        caseData,
        setTemplateSelectedCountMap,
        formState.executeType,
      )
    },
    [
      selectedCaseIds,
      caseIds,
      setSelectedCaseIds,
      getCaseList,
      searchCaseParams,
      selectedTemplateIds,
      caseData,
      setTemplateSelectedCountMap,
      formState.executeType,
    ],
  )

  return {
    onCaseTableChange,
    onTemplateTableChange,
    onCaseSelectAll,
  }
}

// 选择处理hook
export const useSelectionHandlers = (
  getTemplateList: any,
  searchTemplateParams: any,
  templateValue: any,
  formState: any,
  executeTemplateLatestCase: any,
  setSelectedTemplateIds: any,
  setSelectedTemplateData: any,
  setSelectedCaseIds: any,
) => {
  const onSelectAll = useCallback(
    async (checked: boolean) => {
      await handleSelectAll(
        checked,
        getTemplateList,
        searchTemplateParams,
        templateValue,
        formState,
        executeTemplateLatestCase,
        setSelectedTemplateIds,
        setSelectedTemplateData,
        setSelectedCaseIds,
      )
    },
    [
      getTemplateList,
      searchTemplateParams,
      templateValue,
      formState,
      executeTemplateLatestCase,
      setSelectedTemplateIds,
      setSelectedTemplateData,
      setSelectedCaseIds,
    ],
  )

  return {
    onSelectAll,
  }
}

// 计算属性hook
export const useComputedValues = (caseData: any, selectedCaseIds: any) => {
  const isAllSelected = useMemo(
    () => caseData.total > 0 && selectedCaseIds.length === caseData.total,
    [caseData.total, selectedCaseIds.length],
  )

  const isIndeterminate = useMemo(
    () => selectedCaseIds.length > 0 && selectedCaseIds.length < caseData.total,
    [selectedCaseIds.length, caseData.total],
  )

  return {
    isAllSelected,
    isIndeterminate,
  }
}

// 表格配置hook
export const useTableConfig = (
  selectedTemplateIds: any,
  templateIds: any,
  templateData: any,
  selectedTemplateData: any,
  setSelectedTemplateIds: any,
  setSelectedTemplateData: any,
  searchTemplateParams: any,
  templateSelectedCountMap: any,
  selectedCaseIds: any,
  formState: any,
  setTemplateTypeSelectOpen: any,
  templateTypeSelectOpen: any,
  onTemplateSwitchChange: any,
  searchCaseParams: any,
  attackTypeList: any,
  setRiskLevelSelectOpen: any,
  setResourceLevelSelectOpen: any,
  setAttackTypeSelectOpen: any,
  riskLevelSelectOpen: any,
  resourceLevelSelectOpen: any,
  attackTypeSelectOpen: any,
  setDetailVisible: any,
  caseData: any,
  setSelectedCaseIds: any,
  updateTemplateSelectedCountMap: any,
) => {
  const templateRowSelection = useMemo(
    () =>
      createTemplateRowSelection(
        selectedTemplateIds,
        templateIds,
        templateData,
        selectedTemplateData,
        setSelectedTemplateIds,
        setSelectedTemplateData,
      ),
    [
      selectedTemplateIds,
      templateIds,
      templateData,
      selectedTemplateData,
      setSelectedTemplateIds,
      setSelectedTemplateData,
    ],
  )

  const templateColumns = useMemo(
    () =>
      createTemplateColumns(
        searchTemplateParams,
        templateSelectedCountMap,
        selectedCaseIds,
        formState,
        setTemplateTypeSelectOpen,
        templateTypeSelectOpen,
        onTemplateSwitchChange,
      ),
    [
      searchTemplateParams,
      templateSelectedCountMap,
      selectedCaseIds,
      formState,
      templateTypeSelectOpen,
      setTemplateTypeSelectOpen,
      onTemplateSwitchChange,
    ],
  )

  const selectedCaseColumns = useMemo(
    () =>
      createSelectedCaseColumns(
        searchCaseParams,
        attackTypeList,
        setRiskLevelSelectOpen,
        setResourceLevelSelectOpen,
        setAttackTypeSelectOpen,
        riskLevelSelectOpen,
        resourceLevelSelectOpen,
        attackTypeSelectOpen,
        setDetailVisible,
      ),
    [
      searchCaseParams,
      attackTypeList,
      riskLevelSelectOpen,
      resourceLevelSelectOpen,
      attackTypeSelectOpen,
      setRiskLevelSelectOpen,
      setResourceLevelSelectOpen,
      setAttackTypeSelectOpen,
      setDetailVisible,
    ],
  )

  const caseRowSelection = useMemo(
    () =>
      createCaseRowSelection(
        selectedCaseIds,
        caseData,
        setSelectedCaseIds,
        updateTemplateSelectedCountMap,
        formState,
      ),
    [selectedCaseIds, caseData, setSelectedCaseIds, updateTemplateSelectedCountMap, formState],
  )

  return {
    templateRowSelection,
    templateColumns,
    selectedCaseColumns,
    caseRowSelection,
  }
}

// 数据获取效果hooks
export const useDataEffects = (
  getTemplateList: any,
  searchTemplateParams: any,
  templateValue: any,
  formState: any,
  setTemplateData: any,
  setTemplateIds: any,
  getAllTemplateId: any,
  setSearchTemplateParams: any,
  caseValue: any,
  options: any,
  setSearchCaseParams: any,
  selectedTemplateIds: any,
  getCaseList: any,
  searchCaseParams: any,
  setCaseData: any,
  getResourceIdsByTemplateIds: any,
  setCaseIds: any,
  getResourceTagList: any,
  getAttackTypeList: any,
  selectedCaseIds: any,
  setSelectedCaseIds: any,
  setCaseKey: any,
  setTemplateSelectedCountMap: any,
  caseData: any,
  setCaseValue: any,
  templateData: any,
) => {
  // 使用 useRef 跟踪上一次的值，避免不必要的重复请求
  const prevSelectedTemplateIdsRef = useRef<string>('')
  const prevSearchCaseParamsRef = useRef<string>('')
  const isResettingRef = useRef<boolean>(false)
  // 模板数据获取效果
  useEffect(() => {
    getTemplateList({
      ...searchTemplateParams,
      filterParams: {
        name: String(templateValue.value || ''),
        verificationModeIds: [6],
        sceneIds: [2],
        ...searchTemplateParams.filterParams,
      },
    })
      .then(res => {
        const newList = res.data.list.map(item => ({
          ...item,
          key: item.templateId,
          executeUpdate:
            formState.executeType === 3 && formState.executeTemplateLatestCase ? true : false,
        }))
        setTemplateData({
          list: newList,
          total: res.data.total,
        })
      })
      .catch(_ => {})
    const params = { ...searchTemplateParams }
    delete params.page
    delete params.pageSize
    getAllTemplateId({
      filterParams: {
        name: String(templateValue.value || ''),
        verificationModeIds: [6],
        sceneIds: [2],
        ...params.filterParams,
      },
    })
      .then(res => {
        setTemplateIds(res.data.templateIds)
      })
      .catch(_ => {})
  }, [searchTemplateParams, formState.executeType, formState.executeTemplateLatestCase])

  // 模板值变化效果
  useEffect(() => {
    setSearchTemplateParams(prev => ({
      ...prev,
      page: 1,
    }))
  }, [templateValue.value])

  // 用例值变化效果
  useEffect(() => {
    // 如果正在重置参数，不处理 caseValue 的变化
    if (isResettingRef.current) {
      return
    }
    if (caseValue?.field) {
      const selectedOption = options.find(option => option.value === caseValue.field)
      if (selectedOption) {
        const processedValue =
          caseValue.field === 'tagId'
            ? caseValue?.value
              ? (caseValue.value as string[])?.map(item => item.split('$').slice(1).join('$'))
              : undefined
            : caseValue.value
        setSearchCaseParams(prev => {
          if (prev.filterParams?.[caseValue.field] === processedValue) {
            return prev
          }

          // 检查值是否为空（空字符串、null、undefined 或空数组）
          const isEmpty =
            processedValue === '' ||
            processedValue === null ||
            processedValue === undefined ||
            (Array.isArray(processedValue) && processedValue.length === 0)

          // 获取所有 FilterFormSelect 的字段名
          const filterFormFields = options.map(opt => opt.value)

          // 创建新的 filterParams，移除所有 FilterFormSelect 字段
          const newFilterParams = prev.filterParams
            ? Object.keys(prev.filterParams).reduce((acc, key) => {
                // 保留非 FilterFormSelect 字段
                if (!filterFormFields.includes(key)) {
                  acc[key] = prev.filterParams[key]
                }
                return acc
              }, {})
            : {}

          // 如果值为空，只清除 FilterFormSelect 相关字段，保留其他筛选条件
          if (isEmpty) {
            return {
              ...prev,
              page: 1,
              filterParams: newFilterParams,
            }
          }

          // 有值时更新筛选参数
          return {
            ...prev,
            page: 1,
            filterParams: {
              ...newFilterParams,
              [caseValue.field]: processedValue,
            },
          }
        })
      }
    } else if (caseValue?.field === '' && caseValue?.value === '') {
      // 当 caseValue 被重置为空时，清除所有 FilterFormSelect 相关字段
      const filterFormFields = options.map(opt => opt.value)
      setSearchCaseParams(prev => {
        const newFilterParams = prev.filterParams
          ? Object.keys(prev.filterParams).reduce((acc, key) => {
              // 保留非 FilterFormSelect 字段
              if (!filterFormFields.includes(key)) {
                acc[key] = prev.filterParams[key]
              }
              return acc
            }, {})
          : {}
        return {
          ...prev,
          page: 1,
          filterParams: newFilterParams,
        }
      })
    }
  }, [caseValue])

  // 当模板ID变化时，重置用例相关的参数为默认值
  useEffect(() => {
    const currentTemplateIdsStr = selectedTemplateIds.join(',')
    const isTemplateIdsChanged = prevSelectedTemplateIdsRef.current !== currentTemplateIdsStr

    if (isTemplateIdsChanged && selectedTemplateIds.length > 0) {
      // 标记正在重置参数
      isResettingRef.current = true
      // 先重置用例搜索值，避免触发caseValue的useEffect设置filterParams
      setCaseValue({
        field: '',
        value: '',
      })
      // 重置用例搜索参数为默认值（包含基础filterParams，明确设置name为undefined）
      setSearchCaseParams({
        page: 1,
        pageSize: 10,
        filterParams: {
          verificationModeId: [6],
          sceneId: [2],
          name: undefined, // 明确设置name为undefined，确保重置时不包含name字段
        },
      })
      // 清空模板已选用例数映射
      setTemplateSelectedCountMap({})
      // 更新模板ID的 ref
      prevSelectedTemplateIdsRef.current = currentTemplateIdsStr
    }
    // 只在模板ID变化时触发
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplateIds.join(',')])

  // 发送用例相关接口请求
  useEffect(() => {
    // 生成当前值的字符串表示用于比较
    const currentTemplateIdsStr = selectedTemplateIds.join(',')
    const currentSearchParamsStr = JSON.stringify({
      page: searchCaseParams.page,
      pageSize: searchCaseParams.pageSize,
      filterParams: searchCaseParams.filterParams,
    })

    // 如果正在重置参数，使用默认参数发送请求
    if (isResettingRef.current) {
      const defaultFilterParams = {
        verificationModeId: [6],
        sceneId: [2],
      }
      // 过滤掉 undefined 值
      const cleanedFilterParams = Object.keys(defaultFilterParams).reduce((acc, key) => {
        if (defaultFilterParams[key] !== undefined) {
          acc[key] = defaultFilterParams[key]
        }
        return acc
      }, {})

      getCaseList({
        page: 1,
        pageSize: 10,
        queryType: 'query_bind',
        templateId: selectedTemplateIds,
        filterParams: cleanedFilterParams,
      })
        .then(res => {
          setCaseData({
            list: res.data.list,
            total: res.data.total,
          })
        })
        .catch(_ => {})

      getResourceIdsByTemplateIds({
        queryType: 'query_bind',
        templateId: selectedTemplateIds,
        filterParams: cleanedFilterParams,
      })
        .then(res => {
          const resourceIds = res.data.resourceIds
          setCaseIds(resourceIds)
          if (resourceIds.length > 0) {
            getAttackTypeList({
              queryType: 'query_type_create_task_resource_list',
              'createTaskResourceListQuery.templateIds': selectedTemplateIds,
            }).catch(_ => {})
            getResourceTagList({
              queryType: 'query_bind',
              templateId: selectedTemplateIds,
            }).catch(_ => {})
          }

          const allResourceIdsInSelected = resourceIds.every(id => selectedCaseIds.includes(id))
          if (allResourceIdsInSelected) {
            setSelectedCaseIds(resourceIds)
          }
        })
        .catch(_ => {})

      // 重置标记并更新 refs
      isResettingRef.current = false
      prevSearchCaseParamsRef.current = currentSearchParamsStr
      return
    }

    // 检查是否需要发送请求（避免重复请求）
    const isTemplateIdsChanged = prevSelectedTemplateIdsRef.current !== currentTemplateIdsStr
    const isSearchParamsChanged = prevSearchCaseParamsRef.current !== currentSearchParamsStr

    if (!isTemplateIdsChanged && !isSearchParamsChanged) {
      return
    }

    if (selectedTemplateIds.length > 0) {
      // 过滤掉 undefined 值
      const cleanedFilterParams = searchCaseParams.filterParams
        ? Object.keys(searchCaseParams.filterParams).reduce((acc, key) => {
            if (searchCaseParams.filterParams[key] !== undefined) {
              acc[key] = searchCaseParams.filterParams[key]
            }
            return acc
          }, {})
        : {}

      getCaseList({
        ...searchCaseParams,
        queryType: 'query_bind',
        templateId: selectedTemplateIds,
        filterParams: {
          verificationModeId: [6],
          sceneId: [2],
          ...cleanedFilterParams,
        },
      })
        .then(res => {
          setCaseData({
            list: res.data.list,
            total: res.data.total,
          })
        })
        .catch(_ => {})

      const paramsObj = { ...searchCaseParams }
      delete paramsObj.page
      delete paramsObj.pageSize
      const cleanedParamsObjFilterParams = paramsObj.filterParams
        ? Object.keys(paramsObj.filterParams).reduce((acc, key) => {
            if (paramsObj.filterParams[key] !== undefined) {
              acc[key] = paramsObj.filterParams[key]
            }
            return acc
          }, {})
        : {}

      getResourceIdsByTemplateIds({
        ...paramsObj,
        queryType: 'query_bind',
        templateId: selectedTemplateIds,
        filterParams: {
          verificationModeId: [6],
          sceneId: [2],
          ...cleanedParamsObjFilterParams,
        },
      })
        .then(res => {
          const resourceIds = res.data.resourceIds
          setCaseIds(resourceIds)
          if (resourceIds.length > 0) {
            getAttackTypeList({
              queryType: 'query_type_create_task_resource_list',
              'createTaskResourceListQuery.templateIds': selectedTemplateIds,
            }).catch(_ => {})
            getResourceTagList({
              queryType: 'query_bind',
              templateId: selectedTemplateIds,
            }).catch(_ => {})
          }

          const allResourceIdsInSelected = resourceIds.every(id => selectedCaseIds.includes(id))
          if (allResourceIdsInSelected) {
            setSelectedCaseIds(resourceIds)
          }
        })
        .catch(_ => {})

      const params = { ...searchCaseParams }
      delete params.page
      delete params.pageSize
      const cleanedParamsFilterParams = params.filterParams
        ? Object.keys(params.filterParams).reduce((acc, key) => {
            if (params.filterParams[key] !== undefined) {
              acc[key] = params.filterParams[key]
            }
            return acc
          }, {})
        : {}

      // 更新 refs
      prevSelectedTemplateIdsRef.current = currentTemplateIdsStr
      prevSearchCaseParamsRef.current = currentSearchParamsStr
    } else {
      setCaseIds([])
      setCaseData({
        list: [],
        total: 0,
      })
      setSelectedCaseIds([])
      prevSelectedTemplateIdsRef.current = ''
      prevSearchCaseParamsRef.current = ''
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchCaseParams, selectedTemplateIds.join(',')])

  // 用例key更新效果
  useEffect(() => {
    setCaseKey(prev => prev + 1)
  }, [selectedTemplateIds])

  // 监听模板数据变化，如果选中的模板数据变化，重新获取用例数据
  const prevTemplateDataRef = useRef<string>('')
  useEffect(() => {
    if (selectedTemplateIds.length > 0) {
      // 获取当前选中模板的数据摘要（用于检测变化）
      const currentTemplateDataStr = JSON.stringify(
        templateData.list
          .filter(item => selectedTemplateIds.includes(item.templateId))
          .map(item => ({
            templateId: item.templateId,
            resourceAmount: item.resourceAmount,
          }))
          .sort((a, b) => a.templateId.localeCompare(b.templateId)),
      )

      // 如果模板数据变化了，且不是重置参数的情况，重新获取用例数据
      if (
        prevTemplateDataRef.current !== currentTemplateDataStr &&
        prevTemplateDataRef.current !== '' &&
        !isResettingRef.current
      ) {
        // 更新 ref
        prevTemplateDataRef.current = currentTemplateDataStr
        // 清空搜索参数 ref，强制触发用例数据重新获取
        prevSearchCaseParamsRef.current = ''
      } else if (prevTemplateDataRef.current === '') {
        // 首次设置
        prevTemplateDataRef.current = currentTemplateDataStr
      }
    } else {
      prevTemplateDataRef.current = ''
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateData.list, selectedTemplateIds.join(',')])
}

// 提交处理hook
export const useSubmitHandlers = (
  step: number,
  formState: any,
  selectedCaseIds: any,
  selectedTemplateData: any,
  templateData: any,
  selectedTemplateIds: any,
  emailList: any,
  userId: string,
  createTask: any,
  createForm: any,
  navigate: any,
  submitForm: any,
) => {
  const submitHandler = useCallback(
    async (values?: any) => {
      console.log('values', values)
      try {
        let formValues
        if (values) {
          formValues = values
          console.log('formValues', formValues)
        } else {
          try {
            if (createForm && typeof createForm.validateFields === 'function') {
              formValues = await createForm.validateFields()
            } else {
              console.error(t('表单实例不存在或方法不可用，无法进行验证'))
              return
            }
          } catch (validationError: any) {
            console.error(t('表单验证失败:'), validationError)
            if (
              validationError?.message?.includes('__ANT_BUTTON') ||
              validationError?.toString?.()?.includes('__ANT_BUTTON')
            ) {
              console.warn(t('检测到 __ANT_BUTTON 错误，尝试使用备用验证方法'))
              try {
                const formData = createForm?.getFieldsValue?.()
                if (formData) {
                  formValues = formData
                  console.log(t('使用备用方法获取表单数据:'), formValues)
                } else {
                  console.error(t('无法获取表单数据'))
                  return
                }
              } catch (fallbackError) {
                console.error(t('备用方法也失败了:'), fallbackError)
                return
              }
            } else {
              return
            }
          }
        }

        if (step === 1) {
          submitForm()
        } else {
          console.log('formState', formState)
          try {
            const params = buildCreateTaskParams(
              formState,
              selectedCaseIds,
              selectedTemplateData,
              templateData,
              selectedTemplateIds,
              emailList,
              userId,
            )
            console.log('params', params)
            await createTask(params as any)
            Message.success(t('任务创建成功'))
            createForm?.resetFields()
            setTimeout(() => {
              navigate('/validation/network/webgateway?tab=task')
            }, 100)
          } catch (error) {
            console.error(error)
          }
        }
      } catch (error: any) {
        console.error(t('表单验证失败:'), error)
      }
    },
    [
      step,
      formState,
      selectedCaseIds,
      selectedTemplateData,
      templateData,
      selectedTemplateIds,
      emailList,
      userId,
      createTask,
      createForm,
      navigate,
      submitForm,
    ],
  )

  const handleSubmit = useCallback(async (form: any) => {
    try {
      const values = await form.validateFields()
      console.log(t('模板表单数据:'), values)
    } catch (error) {
      console.error(t('表单验证失败:'), error)
    }
  }, [])

  const onSaveTemplateSubmit = useCallback(
    (values: any) => (caseIds: any, saveCaseToTemplate: any, setSaveTemplateModal: any) => {
      saveTemplateSubmit(values, caseIds, saveCaseToTemplate, setSaveTemplateModal)
    },
    [],
  )

  return {
    submitHandler,
    handleSubmit,
    onSaveTemplateSubmit,
  }
}

// 简化的主组件hook
export const useCreateTaskMain = () => {
  const navigate = useNavigate()
  const userId = JSON.parse(localStorage.getItem('token') || '{}').id

  // 使用提取的hooks
  const { formState, setFormState, createForm, form, handleFormValueChange } = useFormState()
  const {
    taskGroup,
    getTaskGroup,
    emailList,
    getAgentList,
    agentList,
    attackTypeList,
    getAttackTypeList,
    createTask,
    createTaskLoading,
    saveCaseToTemplate,
    saveCaseToTemplateLoading,
    getTemplateList,
    templateLoading,
    getCaseList,
    caseLoading,
    resourceTagList,
    getResourceTagList,
    getAllTemplateId,
    getAllCaseId,
    getResourceIdsByTemplateIds,
    getSecurityDeviceList,
    getTaskDetail,
  } = useApiData()
  const { cascaderDeviceListData, fetchSecurityDeviceList, securityDeviceData } =
    useDeviceData(getSecurityDeviceList)
  const {
    executeTemplateLatestCase,
    templateValue,
    setTemplateValue,
    templateData,
    setTemplateData,
    searchTemplateParams,
    setSearchTemplateParams,
    selectedTemplateIds,
    setSelectedTemplateIds,
    selectedTemplateData,
    setSelectedTemplateData,
    templateIds,
    setTemplateIds,
  } = useTemplateData()
  const {
    caseValue,
    setCaseValue,
    caseData,
    setCaseData,
    searchCaseParams,
    setSearchCaseParams,
    selectedCaseIds,
    setSelectedCaseIds,
    caseKey,
    setCaseKey,
    caseIds,
    setCaseIds,
    templateSelectedCountMap,
    setTemplateSelectedCountMap,
    updateTemplateSelectedCountMap,
  } = useCaseData()
  const { step, handleBack, submitForm } = useStepManagement(
    setSelectedTemplateIds,
    setSelectedTemplateData,
    setSelectedCaseIds,
  )
  const { saveTemplateModal, setSaveTemplateModal, detailVisible, setDetailVisible } =
    useModalState()
  const {
    templateTypeSelectOpen,
    setTemplateTypeSelectOpen,
    riskLevelSelectOpen,
    setRiskLevelSelectOpen,
    resourceLevelSelectOpen,
    setResourceLevelSelectOpen,
    attackTypeSelectOpen,
    setAttackTypeSelectOpen,
  } = useSelectState()

  // 初始化数据
  useInitialData(
    getAgentList,
    getTaskGroup,
    getAttackTypeList,
    setFormState,
    createForm,
    getTaskDetail,
    agentList,
    securityDeviceData,
    fetchSecurityDeviceList,
    getSecurityDeviceList,
  )

  // 搜索选项
  const { options, templateSearchOptions } = useSearchOptions(resourceTagList)

  // 数据获取效果hooks
  useDataEffects(
    getTemplateList,
    searchTemplateParams,
    templateValue,
    formState,
    setTemplateData,
    setTemplateIds,
    getAllTemplateId,
    setSearchTemplateParams,
    caseValue,
    options,
    setSearchCaseParams,
    selectedTemplateIds,
    getCaseList,
    searchCaseParams,
    setCaseData,
    getResourceIdsByTemplateIds,
    setCaseIds,
    getResourceTagList,
    getAttackTypeList,
    selectedCaseIds,
    setSelectedCaseIds,
    setCaseKey,
    setTemplateSelectedCountMap,
    caseData,
    setCaseValue,
    templateData,
  )

  // 表单处理hooks
  const { onExecuteTypeChange, onExecuteTemplateLatestCaseChange, onTemplateSwitchChange } =
    useFormHandlers(
      formState,
      setFormState,
      createForm,
      setTemplateData,
      setSelectedTemplateData,
      selectedCaseIds,
      templateSelectedCountMap,
      templateData,
    )

  // 表格处理hooks
  const { onCaseTableChange, onTemplateTableChange, onCaseSelectAll } = useTableHandlers(
    caseValue,
    searchCaseParams,
    setSearchCaseParams,
    setSelectedCaseIds,
    searchTemplateParams,
    setSearchTemplateParams,
    setSelectedTemplateIds,
    selectedCaseIds,
    caseIds,
    getCaseList,
    selectedTemplateIds,
    caseData,
    setTemplateSelectedCountMap,
    formState,
  )

  // 选择处理hooks
  const { onSelectAll } = useSelectionHandlers(
    getTemplateList,
    searchTemplateParams,
    templateValue,
    formState,
    executeTemplateLatestCase,
    setSelectedTemplateIds,
    setSelectedTemplateData,
    setSelectedCaseIds,
  )

  // 计算属性
  const { isAllSelected, isIndeterminate } = useComputedValues(caseData, selectedCaseIds)

  // 提交处理hooks
  const { submitHandler, handleSubmit, onSaveTemplateSubmit } = useSubmitHandlers(
    step,
    formState,
    selectedCaseIds,
    selectedTemplateData,
    templateData,
    selectedTemplateIds,
    emailList,
    userId,
    createTask,
    createForm,
    navigate,
    submitForm,
  )

  // 表格配置hooks
  const { templateRowSelection, templateColumns, selectedCaseColumns, caseRowSelection } =
    useTableConfig(
      selectedTemplateIds,
      templateIds,
      templateData,
      selectedTemplateData,
      setSelectedTemplateIds,
      setSelectedTemplateData,
      searchTemplateParams,
      templateSelectedCountMap,
      selectedCaseIds,
      formState,
      setTemplateTypeSelectOpen,
      templateTypeSelectOpen,
      onTemplateSwitchChange,
      searchCaseParams,
      attackTypeList,
      setRiskLevelSelectOpen,
      setResourceLevelSelectOpen,
      setAttackTypeSelectOpen,
      riskLevelSelectOpen,
      resourceLevelSelectOpen,
      attackTypeSelectOpen,
      setDetailVisible,
      caseData,
      setSelectedCaseIds,
      updateTemplateSelectedCountMap,
    )

  return {
    // 基础数据
    navigate,
    userId,
    formState,
    setFormState,
    createForm,
    form,
    handleFormValueChange,
    step,
    handleBack,
    submitForm,
    cascaderDeviceListData,
    fetchSecurityDeviceList,

    // 模板数据
    executeTemplateLatestCase,
    templateValue,
    setTemplateValue,
    templateData,
    setTemplateData,
    searchTemplateParams,
    setSearchTemplateParams,
    selectedTemplateIds,
    setSelectedTemplateIds,
    selectedTemplateData,
    setSelectedTemplateData,
    templateIds,
    setTemplateIds,

    // 用例数据
    caseValue,
    setCaseValue,
    caseData,
    setCaseData,
    searchCaseParams,
    setSearchCaseParams,
    selectedCaseIds,
    setSelectedCaseIds,
    caseKey,
    setCaseKey,
    caseIds,
    setCaseIds,

    // 模态框状态
    saveTemplateModal,
    setSaveTemplateModal,
    detailVisible,
    setDetailVisible,

    // 选择器状态
    templateTypeSelectOpen,
    setTemplateTypeSelectOpen,
    riskLevelSelectOpen,
    setRiskLevelSelectOpen,
    resourceLevelSelectOpen,
    setResourceLevelSelectOpen,
    attackTypeSelectOpen,
    setAttackTypeSelectOpen,

    // API数据
    taskGroup,
    getTaskGroup,
    emailList,
    getAgentList,
    agentList,
    attackTypeList,
    getAttackTypeList,
    createTask,
    createTaskLoading,
    saveCaseToTemplate,
    saveCaseToTemplateLoading,
    getTemplateList,
    templateLoading,
    getCaseList,
    caseLoading,
    resourceTagList,
    getResourceTagList,
    getAllTemplateId,
    getAllCaseId,
    getResourceIdsByTemplateIds,

    // 搜索选项
    options,
    templateSearchOptions,

    // 处理函数
    onExecuteTypeChange,
    onExecuteTemplateLatestCaseChange,
    onTemplateSwitchChange,
    onCaseTableChange,
    onTemplateTableChange,
    onCaseSelectAll,
    onSelectAll,
    isAllSelected,
    isIndeterminate,
    submitHandler,
    handleSubmit,
    onSaveTemplateSubmit,

    // 表格配置
    templateRowSelection,
    templateColumns,
    selectedCaseColumns,
    caseRowSelection,
  }
}
